import type { Point } from 'leaflet'

export type LeafletHitTester = {
  name: string
  /** Higher priority wins over the shared vector canvas (priority 0). */
  priority: number
  hitAt: (containerPoint: Point) => boolean
}

export type LeafletHitRouter = {
  register(tester: LeafletHitTester): void
  unregister(name: string): void
  /** A tester above the vector canvas claims this pixel (track dot over a polygon). */
  occludesVector(containerPoint: Point): boolean
  /** One cursor writer. Several sources can hover; pointer stays until the last one leaves. */
  setHover(name: string, on: boolean): void
}

/**
 * Decides which backend owns a pointer event when several canvases are on the map.
 * Vector shapes still use Leaflet's own draw-order hit test. This router only
 * suppresses that hit when something stacked above the vector canvas is under the cursor.
 */
export function createLeafletHitRouter(
  setCursorPointer: (on: boolean) => void,
): LeafletHitRouter {
  const testers: LeafletHitTester[] = []
  const hovering = new Set<string>()

  function paintCursor(): void {
    setCursorPointer(hovering.size > 0)
  }

  return {
    register(tester) {
      const existing = testers.findIndex((t) => t.name === tester.name)
      if (existing >= 0) testers.splice(existing, 1)
      testers.push(tester)
    },

    unregister(name) {
      const index = testers.findIndex((t) => t.name === name)
      if (index >= 0) testers.splice(index, 1)
      if (hovering.delete(name)) paintCursor()
    },

    occludesVector(containerPoint) {
      return testers.some((tester) => tester.priority > 0 && tester.hitAt(containerPoint))
    },

    setHover(name, on) {
      const had = hovering.has(name)
      if (on) hovering.add(name)
      else hovering.delete(name)
      if (had !== on) paintCursor()
    },
  }
}
